/**
 * Adds the product's first-order free gifts (rules from the 47-skin-functions app) with the
 * main line on ATC, for gifts matching the chosen purchase type. One cart/add request (main
 * first, then gifts). Blocks theme ProductForm duplicate submit. The `_subscription_gift`
 * property only marks the line; the app's discount function decides whether it's free.
 * Gifts granted by a discount code get that code applied to the cart after the add.
 */
(function () {
  const CONFIG_SELECTOR = 'script[data-skio-v2-subscription-gifts-atc]';
  const SUBMIT_FLAG = 'skioV2GiftsSubmit';

  function parseConfig(script) {
    try {
      return JSON.parse(script.textContent.trim());
    } catch {
      return null;
    }
  }

  function getForm(config) {
    if (!config?.formId) return null;
    return (
      document.getElementById(config.formId) ||
      document.forms[config.formId] ||
      null
    );
  }

  function queryFormField(form, name) {
    const formId = form.getAttribute('id');
    if (formId) {
      const associated = document.querySelector(`[form="${formId}"][name="${name}"]`);
      if (associated && !associated.disabled) return associated;
    }
    return (
      form.querySelector(`[name="${name}"]:not([disabled])`) ||
      form.querySelector(`[name="${name}"]`)
    );
  }

  function collectLineProperties(form) {
    const properties = {};
    const formId = form.getAttribute('id');

    const fields = formId
      ? [
          ...form.querySelectorAll('input, textarea, select'),
          ...document.querySelectorAll(`[form="${formId}"]`),
        ]
      : [...form.querySelectorAll('input, textarea, select')];

    fields.forEach((el) => {
      const name = el.name;
      if (!name || !name.startsWith('properties[')) return;
      if (el.disabled) return;
      const match = name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      if (el.type === 'checkbox' && !el.checked) return;
      if (el.value === '') return;
      properties[match[1]] = el.value;
    });

    return properties;
  }

  function buildMainLineItem(form) {
    const idInput = queryFormField(form, 'id');
    if (!idInput?.value) return null;

    const quantityInput = queryFormField(form, 'quantity');
    const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
    const sellingPlanInput = queryFormField(form, 'selling_plan');
    const sellingPlanId = sellingPlanInput?.value ? parseInt(sellingPlanInput.value, 10) : null;

    const item = {
      id: parseInt(idInput.value, 10),
      quantity,
    };

    if (sellingPlanId) {
      item.selling_plan = sellingPlanId;
    }

    const properties = collectLineProperties(form);
    if (Object.keys(properties).length) {
      item.properties = properties;
    }

    return item;
  }

  function matchingGifts(form, config) {
    if (!Array.isArray(config?.gifts)) return [];

    const purchaseType = queryFormField(form, 'selling_plan')?.value ? 'subscription' : 'one_time';

    const variantGid = `gid://shopify/ProductVariant/${queryFormField(form, 'id')?.value}`;

    return config.gifts
      .filter((g) => g?.variantId && (g.purchaseType === 'both' || g.purchaseType === purchaseType))
      // A code the customer enters themselves adds its gift (free-gift-rules.js) once the code is on the cart
      .filter((g) => !g.code || g.autoApply)
      .filter(
        (g) => !g.triggerIds?.length || g.triggerIds.includes(variantGid) || g.triggerIds.includes(config.productGid)
      );
  }

  function buildGiftLineItems(form, config) {
    return matchingGifts(form, config).map((g) => ({
      id: parseInt(g.variantId, 10),
      quantity: 1,
      properties: {
        _subscription_gift: 'true',
      },
    }));
  }

  /** Code gifts are only discounted with their code on the cart; keeps codes already applied. */
  async function applyGiftDiscountCodes(root, form, config) {
    const giftCodes = matchingGifts(form, config)
      .map((g) => g.code)
      .filter(Boolean);
    if (!giftCodes.length) return;

    const cart = await (await fetch(`${root}cart.js`)).json();
    const codes = new Set([...(cart.discount_codes || []).map((discount) => discount.code), ...giftCodes]);
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...codes].join(',') }),
    });
  }

  // One of each gift per cart: a gift already on the cart is not added again.
  async function withoutGiftsInCart(root, giftItems) {
    const cart = await (await fetch(`${root}cart.js`)).json();
    const inCart = new Set(
      cart.items.filter((item) => item.properties?._subscription_gift).map((item) => item.variant_id)
    );
    return giftItems.filter((gift) => !inCart.has(gift.id));
  }

  function setSubmitBusy(form, busy) {
    Array.from(form.elements)
      .filter((el) => el.type === 'submit')
      .forEach((btn) => {
        if (busy) {
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.removeAttribute('aria-busy');
        }
      });
  }

  async function postCartAdd(root, payload) {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    return { response, responseJson: await response.json() };
  }

  async function dispatchCartSuccess(form, addedItems, onSuccessDo) {
    const root = window.Shopify?.routes?.root || '/';
    const cartContent = await (await fetch(`${root}cart.js`)).json();

    const detail = {
      items: addedItems,
      cart: cartContent,
      onSuccessDo,
      fromSkioV2SubscriptionGifts: true,
    };

    form.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail,
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo,
          cart: cartContent,
          fromSkioV2SubscriptionGifts: true,
        },
      })
    );
  }

  async function submitWithSubscriptionGifts(form, config) {
    const mainItem = buildMainLineItem(form);
    if (!mainItem?.id) return;

    const root = window.Shopify?.routes?.root || '/';
    const giftItems = await withoutGiftsInCart(root, buildGiftLineItems(form, config));
    const items = [mainItem, ...giftItems];
    const onSuccessDo = queryFormField(form, 'on_success')?.value || null;

    setSubmitBusy(form, true);
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const idInput = queryFormField(form, 'id');

    try {
      const payload = { items };

      if (sectionsToBundle.length) {
        payload.sections = sectionsToBundle.join(',');
        payload.sections_url = `${root}variants/${idInput.value}`;
      }

      const { response, responseJson } = await postCartAdd(root, payload);

      if (!response.ok) {
        form.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              error: responseJson?.description || responseJson?.message,
            },
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      await applyGiftDiscountCodes(root, form, config);

      const addedItems = Array.isArray(responseJson.items)
        ? responseJson.items
        : [responseJson];

      if (
        window.themeVariables?.settings?.cartType === 'page' ||
        window.themeVariables?.settings?.pageType === 'cart'
      ) {
        window.location.href = `${root}cart`;
        return;
      }

      await dispatchCartSuccess(form, addedItems, onSuccessDo);
    } catch (error) {
      console.warn('[Skio V2 gifts ATC]', error);
    } finally {
      delete form.dataset[SUBMIT_FLAG];
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true })
      );
      setSubmitBusy(form, false);
    }
  }

  function shouldHandleSubmit(form, config) {
    return buildGiftLineItems(form, config).length > 0;
  }

  function bindForm(form, config) {
    if (!form || form.dataset.skioV2GiftsAtcBound === 'true') return;
    form.dataset.skioV2GiftsAtcBound = 'true';

    form.addEventListener(
      'submit',
      (event) => {
        if (event.submitter?.getAttribute('aria-busy') === 'true') return;
        if (!shouldHandleSubmit(form, config)) return;

        form.dataset[SUBMIT_FLAG] = 'true';
        event.preventDefault();
        event.stopImmediatePropagation();

        void submitWithSubscriptionGifts(form, config);
      },
      { capture: true }
    );
  }

  function init() {
    document.querySelectorAll(CONFIG_SELECTOR).forEach((script) => {
      const config = parseConfig(script);
      if (!config?.gifts?.length) return;
      const form = getForm(config);
      if (form) bindForm(form, config);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
